import {
  useMutation,
  useQueryClient,
  UseMutationResult,
  UseMutationOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import type {
  AssignTierDto,
  OrganizationMembershipDto,
  ResponseDto,
} from "@mimber/schema"
import { membersKeys } from "../members.utils"

interface AssignMemberTierOptions extends Omit<
  UseMutationOptions<
    ResponseDto<OrganizationMembershipDto>,
    Error,
    AssignTierDto
  >,
  "mutationFn"
> {}

export const useAssignMemberTier = (
  memberUuid: string,
  options: AssignMemberTierOptions = {}
): UseMutationResult<
  ResponseDto<OrganizationMembershipDto>,
  Error,
  AssignTierDto
> => {
  guardAppUsage("mimber-organization", "useAssignMemberTier")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async (data: AssignTierDto) => {
      const response = await server.patch<
        ResponseDto<OrganizationMembershipDto>
      >(`members/${memberUuid}/tier`, data)
      return response.data
    },
    onSuccess: (response, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: membersKeys.findAll() })
      queryClient.invalidateQueries({
        queryKey: membersKeys.findOne(memberUuid),
      })
      queryClient.invalidateQueries({
        queryKey: membersKeys.getMemberDuesSummary(memberUuid),
      })
      options?.onSuccess?.(response, variables, onMutateResult, context)
    },
  })
}
